import React from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef, Getter } from "@tanstack/react-table";
import type { FiltersDropDownItem } from "../../../../components/FiltersDropDown/FiltersDropDown";
import { useListPageState } from "./useListPageState";
import { useListPageHandlers } from "./useListPageHandlers";
import { useListPageFilters } from "./useListPageFilters";
import { useListPageData } from "./useListPageData";
import { MailIcon } from "../../../../components/Icons/Icons";
import styled from "styled-components";

export const IconWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

export const NumberBadge = styled.div`
  position: absolute;
  top: -5px;
  right: -5px;
  background-color: ${({ theme }) => theme.colors.primaryBlue};
  color: ${({ theme }) => theme.colors.white};
  border-radius: 50%;
  width: 16px;
  height: 16px;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  display: flex;
  justify-content: center;
  align-items: center;
`;

export interface ListPageConfig<TData, TResponse> {
  // State configuration
  defaultPerPage?: number;
  perPageOptions?: number[];

  // Data configuration
  endpoint: string;
  requestParams?: { [key: string]: string };
  statusTransformer?: (status: string[]) => string[];
  dataTransformer: (response: TResponse) => TData[];
  paginationTransformer: (response: TResponse) => {
    total: number;
    offset: number;
  };
  getUnreadIds?: (item: TData) => boolean;
  notifications?: { ids: string[] };

  // Filter configuration
  statusFilters: FiltersDropDownItem[];
  accountManagerFilters: FiltersDropDownItem[];
  customerServiceRepFilters: FiltersDropDownItem[];

  // Table configuration
  columns: ColumnDef<TData>[];
  custom_columns?: { [accessorKey: string]: number };

  // Navigation
  basePath: string;

  // Search configuration
  searchPlaceholder: string;
}

export interface ListPageReturn<TData> {
  // State
  state: ReturnType<typeof useListPageState>;
  data: TData[];
  setData: (data: TData[]) => void;
  isLoading: boolean;
  error: any;
  mutate: () => void;

  // Handlers
  handlers: ReturnType<typeof useListPageHandlers>;

  // Computed values
  hasUnreadMessages: boolean;
  hasAccountManagersCSM: boolean;

  // Table configuration
  tableColumns: ColumnDef<TData>[];
}

/**
 * Comprehensive hook that manages all aspects of a list page
 */
export const useListPage = <TData, TResponse>(
  config: ListPageConfig<TData, TResponse>
): ListPageReturn<TData> => {
  const { t } = useTranslation();

  // Initialize state
  const state = useListPageState({
    defaultPerPage: config.defaultPerPage,
    perPageOptions: config.perPageOptions,
  });

  // Initialize data management
  const { data, setData, isLoading, error, mutate } = useListPageData(state, {
    endpoint: config.endpoint,
    requestParams: config.requestParams,
    statusTransformer: config.statusTransformer,
    dataTransformer: config.dataTransformer,
    paginationTransformer: config.paginationTransformer,
    getUnreadIds: config.getUnreadIds,
    notifications: config.notifications,
  });

  // Initialize handlers
  const handlers = useListPageHandlers(state, setData);

  // Initialize filters
  useListPageFilters(state, {
    statusFilters: config.statusFilters,
    accountManagerFilters: config.accountManagerFilters,
    customerServiceRepFilters: config.customerServiceRepFilters,
  });

  // Computed values
  const hasUnreadMessages = data.some(
    (item: any) => item.unread_message_count !== 0
  );

  const hasAccountManagersCSM = data.some(
    (item: any) => item.account_manager_csm !== "--"
  );

  // Enhanced table columns with conditional columns
  const tableColumns: ColumnDef<TData>[] = React.useMemo(() => {
    const columns = [...config.columns];
    if (config.custom_columns) {
      if (
        hasAccountManagersCSM &&
        config.custom_columns.account_manager_csm !== undefined
      ) {
        columns.splice(config.custom_columns.account_manager_csm, 0, {
          header: t("Account Manager/CSM"),
          accessorKey: "account_manager_csm",
          id: "account_manager_csm",
        } as ColumnDef<TData>);
      }
      if (
        hasUnreadMessages &&
        config.custom_columns.unread_message_count !== undefined
      ) {
        const position = config.custom_columns.unread_message_count;
        columns.splice(hasAccountManagersCSM ? position + 1 : position, 0, {
          header: "",
          accessorKey: "unread_message_count",
          width: 60,
          cell: ({ getValue }: { getValue: Getter<number> }) => {
            const count = getValue();
            return count !== 0 ? (
              <IconWrapper>
                <MailIcon width={24} height={24} />
                <NumberBadge>{count}</NumberBadge>
              </IconWrapper>
            ) : null;
          },
        } as ColumnDef<TData>);
      }
    }
    return columns;
  }, [
    config.columns,
    config.custom_columns,
    hasAccountManagersCSM,
    hasUnreadMessages,
    t,
  ]);

  return {
    state,
    data,
    setData,
    isLoading,
    error,
    mutate,
    handlers,
    hasUnreadMessages,
    hasAccountManagersCSM,
    tableColumns,
  };
};
